/**
 * **One table contract — X-11, and Dishant's E-6.**
 *
 * > *"Make Columns and Column Headers consistent in terms of Text Size,
 * > Alignment, Bolding, ETC."* — 11 Sep
 *
 * He was right, and the count was worse than the note implied. Seven different
 * header treatments were live across eleven tables, because each arrived in the
 * phase that needed it:
 *
 * | Treatment | Where |
 * |---|---|
 * | `text-xs font-semibold uppercase`, 0.04em | Stories · Spotlights · Channel Daily |
 * | the same at **0.06em** | Admin Users |
 * | `text-xs` label colour, **not** uppercase, `0 0.75rem 0.5rem` | Raw Data · Matches |
 * | the same at **0.4rem** bottom padding | Connections |
 * | a `headCell` object local to the file | Revenue |
 * | `text-[0.6rem] font-normal` | Channel coverage grid |
 * | `text-xs font-medium` | the same grid's row labels |
 *
 * None of that was a decision. This module is the decision: **size, weight,
 * casing, tracking, colour, padding and alignment are settled here and nowhere
 * else**, so the next table cannot invent an eighth.
 *
 * ### Why uppercase won
 *
 * It is what the three dense contracted tables already used, it matches the
 * design system's `--tracking-label` (*"the small uppercase KPI labels"*), and
 * Dishant's own note allows it — *"If charts have all caps that's fine but need
 * to have consistent title casing."* The **source strings** stay Title Case, so
 * X-9 is unaffected and nothing is lost if the transform is ever dropped.
 *
 * ### What is deliberately not here
 *
 * Sorting, pagination and filtering. Those differ per table for real reasons —
 * TanStack drives four of them and three are plain markup — and folding them in
 * would turn a styling fix into a rewrite of every dense screen at once. This
 * settles how a table *looks*; each table keeps its own behaviour.
 */
import type { CSSProperties } from 'react'

/**
 * The column header.
 *
 * `position: sticky` is intentionally **not** here — it needs a scroll container
 * to be sticky against, and only the long tables have one. Those pass it
 * themselves.
 */
export const headCell: CSSProperties = {
  padding: '0 0.5rem 0.5rem',
  color: 'var(--text-label)',
  borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap',
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  // The design system's own label tracking. Uppercase text needs it to stay
  // readable at 12px; without it the letters crowd.
  letterSpacing: '0.06em',
  textAlign: 'left',
}

/** A header over numbers. Right-aligned so the digits line up with the cells. */
export const numericHeadCell: CSSProperties = { ...headCell, textAlign: 'right' }

export const bodyCell: CSSProperties = {
  padding: '0.45rem 0.5rem',
  borderTop: '1px solid var(--border)',
  whiteSpace: 'nowrap',
}

/**
 * A numeric cell — right-aligned, with **tabular figures**.
 *
 * `font-variant-numeric: tabular-nums` is load-bearing rather than decorative:
 * Plus Jakarta Sans is proportional, so without it a column of figures does not
 * align on the decimal and scanning it for an outlier stops working. The design
 * system requires numbers in Plus Jakarta Sans rather than mono, and this is
 * what makes that hold up in a table.
 */
export const numericCell: CSSProperties = {
  ...bodyCell,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
}

/** A row-header cell — the channel or dataset name that identifies the row. */
export const rowHeadCell: CSSProperties = {
  ...bodyCell,
  textAlign: 'left',
  fontWeight: 500,
  color: 'var(--text-primary)',
}

/** Pick the right header style from the column's own numeric flag. */
export function headStyle(numeric: boolean | undefined): CSSProperties {
  return numeric ? numericHeadCell : headCell
}
